import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  DxDataGridModule,
  DxDataGridComponent,
  DxTabsModule,
  DxButtonModule,
  DxButtonGroupModule,
  DxNumberBoxModule,
  DxDateBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
} from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { environment } from 'src/environments/environment';
import { CrudService } from '../../services/crud.service';
import { DiscountValueCellComponent } from '../shared/discount-value-cell/discount-value-cell.component';
import { DiscountDeltaCellComponent } from '../shared/discount-delta-cell/discount-delta-cell.component';
import { StatusBadgeComponent } from '../shared/status-badge/status-badge.component';
import { OutlierBadgeComponent } from '../shared/outlier-badge/outlier-badge.component';
import {
  CustomerDashboardCounts,
  CustomerNegotiationDto,
  CustomerSupplierDto,
  CustomerTab,
} from '../shared/negotiation-dto.model';
import {
  CustomerPricingFilterService,
  DatePreset,
  DeltaBand,
} from '../shared/customer-pricing-filter.service';

/** Group-by mode for the customer grids (Story 4.3). Shared across the three tabs. */
type GroupByMode = 'catalogue' | 'promotion' | 'supplier' | 'none';

/** Per-tab list state — rows, whether it has been loaded, and its in-flight flag. */
interface TabState {
  rows: CustomerNegotiationDto[];
  loaded: boolean;
  loading: boolean;
  error: boolean;
}

/** Working state for an inline / modal discount form (single Update + Bulk Update). */
interface DiscountFormState {
  value: number | null;
  error: string | null;
  submitting: boolean;
}

/**
 * Customer Pricing dashboard (Story 3.1, design-spec §3b–§3e).
 *
 * The customer's central discount-verification hub, built in the Story 1.4 mount point. Renders
 * (read-only) inside the existing app shell:
 *  - four summary cards bound to `GET /api/customer/dashboard`;
 *  - a pill-tab bar — Pending Review (default) / Update Requests / Accepted — each with a count
 *    badge; and
 *  - one catalogue-grouped DevExtreme DataGrid per tab, listing the tab's rows via the single
 *    Story 1.3 customer DTO (`GET /api/customer/negotiations?tab=...`).
 *
 * Everything is `%` only — never a `$`, list/unit/case price (BR-07). Write actions (Accept/Update/
 * Confirm/Counter/Reminder, bulk toolbar) landed with Stories 3.2–3.5; the threshold config + suppliers
 * directory with 3.6–3.7; the supplier filter pill bar with Story 4.2; the switchable Group-By control
 * (Catalogue / Supplier / None) + collapsible group headers with collapsed pp-delta summaries with
 * Story 4.3 (this file); the Story 4.4 filter chips; and the §4.8 Discount Delta (min–max pp) filter
 * with Story 4.5 (this file) — a client-side range over the loaded rows on all three tabs, composed
 * with the chips in one `rowPassesFilters` path, driving the filtered tab-count badges; and the Accepted
 * date filter with Story 4.6 (this file) — a tab-local client-side `effective_date` predicate over the
 * Accepted rows (five BR-13 presets + a custom range), composed in the same `rowPassesFilters` path and
 * driving the Accepted badge. The reused Story 1.5 primitives (value/delta/status/outlier cells)
 * render the cells; the Story 1.4 layout/status CSS supplies the responsive + status vocabulary.
 */
@Component({
  selector: 'app-customer-pricing',
  templateUrl: './customer-pricing.component.html',
  styleUrls: ['../shared/pricing-layout.css', './customer-pricing.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    DxTabsModule,
    DxDataGridModule,
    DxButtonModule,
    DxButtonGroupModule,
    DxNumberBoxModule,
    DxDateBoxModule,
    DxPopupModule,
    DxSelectBoxModule,
    DiscountValueCellComponent,
    DiscountDeltaCellComponent,
    StatusBadgeComponent,
    OutlierBadgeComponent,
  ],
  standalone: true,
})
export class CustomerPricingComponent implements OnInit, OnDestroy {
  /** Summary-card counts; null until the dashboard endpoint resolves. */
  counts: CustomerDashboardCounts | null = null;
  countsLoading = true;
  /** True when the dashboard-counts fetch failed — lets the cards show an error rather than a misleading 0. */
  countsError = false;

  /** Tab order — Pending Review is the default (index 0). */
  readonly tabs: CustomerTab[] = ['pending', 'update-requests', 'accepted'];
  selectedIndex = 0;

  /** Per-tab list state, keyed by tab. */
  readonly state: Record<CustomerTab, TabState> = {
    pending: this.emptyState(),
    'update-requests': this.emptyState(),
    accepted: this.emptyState(),
  };

  /**
   * Per-tab chip-filtered view of the rows (Story 4.4). The Pending Review + Update Requests grids bind
   * their `[dataSource]` to this (NOT `state[tab].rows`) so the BR-39 filter chips bucket the loaded set
   * client-side. Recomputed imperatively — only when chips change or a tab's rows (re)load — never via a
   * getter (a getter returns a fresh array reference every change-detection pass, which would make
   * DevExtreme reset grouping/selection/expanded-detail and thrash at 3K rows). The Accepted grid keeps
   * binding to `state.accepted.rows` (no chip bar there), but we keep `displayRows.accepted` in sync
   * verbatim for completeness.
   */
  readonly displayRows: Record<CustomerTab, CustomerNegotiationDto[]> = {
    pending: [],
    'update-requests': [],
    accepted: [],
  };

  /** Connected suppliers (Story 3.7 endpoint) — the source for the Story 4.2 supplier filter. */
  suppliers: CustomerSupplierDto[] = [];
  /**
   * Stable, pre-filtered options array bound to the supplier dropdown's `[dataSource]` (Story 4.2).
   * Computed ONCE per suppliers load — NOT via a getter. A getter that returns `suppliers.filter(...)`
   * hands DevExtreme a fresh array reference every change-detection pass, which makes dx-select-box
   * re-initialise its list on every keystroke — breaking the type-ahead search, scroll position, and
   * option selection. Maintaining it imperatively keeps the reference stable (mirrors `displayRows`).
   */
  supplierOptions: CustomerSupplierDto[] = [];
  /** True once the suppliers fetch has resolved (success or failure) — gates the pill bar. */
  suppliersLoaded = false;

  /**
   * Monotonic token bumped on every supplier-filter change. Each loadTab() captures it and discards
   * its response if a newer selection has superseded it — preventing a slow in-flight load (e.g. from
   * a rapid pill A → B click, or a non-current tab reset mid-flight) from caching stale rows against
   * the current filter (code-review 2026-06-09, last-write-wins guard).
   */
  private filterGeneration = 0;

  /** Decorative skeleton rows shown while a tab loads. */
  readonly skeletonRows = [0, 1, 2, 3, 4];

  /** The Pending Review grid instance — for programmatic expand/collapse + selection control. */
  @ViewChild('pendingGrid') pendingGrid?: DxDataGridComponent;

  /** The Update Requests grid instance — for the post-confirm green row flash + repaint (Story 3.3). */
  @ViewChild('updateRequestsGrid') updateRequestsGrid?: DxDataGridComponent;

  /** The Accepted grid instance (Story 4.3) — for re-applying remembered group-collapse state. */
  @ViewChild('acceptedGrid') acceptedGrid?: DxDataGridComponent;

  // ===================================================================== Group-by (Story 4.3)

  /**
   * Active group-by mode (Story 4.3, AC1/AC4). ONE field shared by all three tabs (they live in one
   * component instance), so the choice persists across tab switches automatically and — being a plain
   * in-memory field — resets to 'catalogue' on a full page reload (BR-40). NEVER persisted to
   * local/sessionStorage (both survive a reload — see the story Dev Notes → Persistence).
   */
  groupBy: GroupByMode = 'catalogue';

  /** Options of the Group-By segmented control (DevExtreme dx-button-group). */
  readonly groupByItems: ReadonlyArray<{ key: GroupByMode; text: string }> = [
    { key: 'catalogue', text: 'Catalogue' },
    { key: 'promotion', text: 'Promotion' },
    { key: 'supplier', text: 'Supplier' },
    { key: 'none', text: 'None' },
  ];

  /**
   * Per-tab set of collapsed group keys (Story 4.3, AC4). Each key is the JSON of DevExtreme's group
   * key (e.g. `["WINE ESSENTIALS"]`), so collapse state survives a tab switch within the session and
   * resets on reload (in-memory). Cleared when the group-by mode changes (the re-grouped view starts
   * fully expanded, autoExpandAll).
   */
  readonly collapsed: Record<CustomerTab, Set<string>> = {
    pending: new Set<string>(),
    'update-requests': new Set<string>(),
    accepted: new Set<string>(),
  };

  /**
   * Per-tab flag: when a tab's grid next reaches onContentReady, re-apply its remembered collapse set.
   * Set true on init and whenever a tab is (re-)entered; cleared after the one re-apply so a user's
   * subsequent expand/collapse and ordinary repaints are not fought.
   */
  private readonly collapseReapply: Record<CustomerTab, boolean> = {
    pending: true,
    'update-requests': true,
    accepted: true,
  };

  /** Update Requests rows with an in-flight Confirm (disables the row's Confirm button). */
  confirmingIds = new Set<number>();

  /** Pending Review rows with an in-flight Send/Resend Reminder (disables the row's link, Story 3.5). */
  remindingIds = new Set<number>();

  /** Ids flashing green immediately after a confirm (≈300ms) before they leave the Update Requests tab. */
  confirmFlashingIds = new Set<number>();

  /** The Update Requests row whose inline counter form is open (one at a time), or null (Story 3.4). */
  counterRow: CustomerNegotiationDto | null = null;
  counterForm: DiscountFormState = this.emptyForm();

  /** Rows currently selected in the Pending Review grid (drives the bulk action toolbar). */
  selectedPendingRows: CustomerNegotiationDto[] = [];

  /** Ids flashing green immediately after an accept (≈300ms) before they leave the tab. */
  flashingIds = new Set<number>();

  /** Pending flash/remove timers — cleared on destroy so a late callback can't mutate a dead view. */
  private flashTimers = new Set<ReturnType<typeof setTimeout>>();

  /** Ids of rows a bulk action could not act on (failed_ids) — marked inline until the next action (AC9). */
  failedIds = new Set<number>();

  /** The Pending Review row whose inline Update form is open (one at a time), or null. */
  updateRow: CustomerNegotiationDto | null = null;
  updateForm: DiscountFormState = this.emptyForm();

  /** Bulk Accept confirmation modal. */
  bulkAcceptVisible = false;
  bulkAcceptSubmitting = false;

  /** Bulk Update modal + its form. */
  bulkUpdateVisible = false;
  bulkUpdateForm: DiscountFormState = this.emptyForm();

  private apiUrl: string = environment.baseApiUrl;

  constructor(
    private crudService: CrudService,
    private filterState: CustomerPricingFilterService,
  ) {}

  private emptyForm(): DiscountFormState {
    return { value: null, error: null, submitting: false };
  }

  /**
   * Hide the master-detail expand command column on the Pending Review grid — the inline Update
   * form is opened from the row's "Update" button (expandRow), not a per-row expand arrow.
   */
  readonly hideExpandColumn = (columns: Array<{ type?: string; visible?: boolean }>): void => {
    const expand = columns.find((c) => c.type === 'detailExpand');
    if (expand) {
      expand.visible = false;
    }
  };

  /**
   * Searchable value for the Product column (bound via `[calculateCellValue]`). The column renders
   * through a cellTemplate and has no `dataField`, so the grid's search panel would otherwise never
   * match it. Returning name + code lets the toolbar search hit both. Rendering is unaffected — the
   * cellTemplate reads `cell.data`, not this computed value.
   */
  readonly productSearchValue = (row: CustomerNegotiationDto): string =>
    [row.product_name, row.product_code].filter(Boolean).join(' ');

  async ngOnInit(): Promise<void> {
    // Load the cards + supplier filter pills first, then reconcile the persisted selection against the
    // refreshed pill list BEFORE the first tab load reads it. The active supplier selection is held in
    // the root-scoped filter service (Story 4.2 / BR-40), so a route re-entry restores it while a full
    // page reload resets it to "All". If the previously-selected supplier no longer has a pill (its
    // last active review was actioned since the last visit), reconcileSupplierSelection() resets it to
    // "All" so we never load a tab against a supplier with no visible/clearable pill.
    await Promise.all([this.loadCounts(), this.loadSuppliers()]);
    this.reconcileSupplierSelection();
    // Rehydrate the custom date pickers from any persisted range (Story 4.6, AC7 — route re-entry).
    this.syncDateInputsFromState();
    await this.loadTab('pending');
  }

  ngOnDestroy(): void {
    // Cancel any in-flight flash/remove timers so their callbacks can't fire on a destroyed view.
    this.flashTimers.forEach((t) => clearTimeout(t));
    this.flashTimers.clear();
  }

  private emptyState(): TabState {
    return { rows: [], loaded: false, loading: false, error: false };
  }

  /** The currently selected tab key. */
  get currentTab(): CustomerTab {
    return this.tabs[this.selectedIndex] ?? 'pending';
  }

  get currentState(): TabState {
    return this.state[this.currentTab];
  }

  /** The active supplier filter (supplier_code), or null = "All". Held in the root-scoped service (BR-40). */
  get selectedSupplierCode(): string | null {
    return this.filterState.supplierCode;
  }

  /**
   * The supplier filter pills (Story 4.2, AC1) — one per connected supplier with at least one active
   * review. The `customer/suppliers` endpoint already orders alphabetically by `supplier_name`, so no
   * client re-sort is needed; a supplier with no active reviews shows no pill.
   */
  get supplierPills(): CustomerSupplierDto[] {
    return this.supplierOptions;
  }

  /**
   * Whether the pill bar should render. Visible once suppliers have loaded AND there is at least one
   * pill — OR a supplier filter is currently active. The second clause guarantees the "All" pill is
   * always reachable even if the selected supplier's pill has since vanished (active_reviews_count → 0),
   * so the user can never be stuck with an applied-but-invisible filter (code-review 2026-06-09).
   */
  get showSupplierBar(): boolean {
    return this.suppliersLoaded && (this.supplierPills.length > 0 || this.selectedSupplierCode !== null);
  }

  /**
   * Reconcile the persisted supplier selection (root-scoped, BR-40) against the freshly-loaded pill
   * list. If the previously-selected supplier no longer has a pill, reset to "All" — otherwise the
   * filter would stay silently applied with no selectable pill. Called once per init, after the
   * suppliers fetch resolves and before the first tab load reads the selection.
   */
  private reconcileSupplierSelection(): void {
    const code = this.filterState.supplierCode;
    if (code !== null && !this.supplierPills.some((s) => s.supplier_code === code)) {
      this.filterState.supplierCode = null;
    }
  }

  /** True when the given pill (or the "All" pill, code = null) is the active selection (AC1/AC6). */
  isSupplierSelected(code: string | null): boolean {
    return this.selectedSupplierCode === code;
  }

  // ===================================================================== Group-by (Story 4.3)

  /**
   * groupIndex for the catalogue column (Story 4.3, AC1). `0` when grouping by catalogue (the column
   * becomes the group header and is hidden from the row body by DevExtreme); `undefined` otherwise (the
   * column shows in the body). Bound on every grid's catalogue column.
   */
  get catalogueGroupIndex(): number | undefined {
    return this.groupBy === 'catalogue' ? 0 : undefined;
  }

  /** groupIndex for the supplier column — `0` when grouping by supplier, else `undefined` (AC1). */
  get supplierGroupIndex(): number | undefined {
    return this.groupBy === 'supplier' ? 0 : undefined;
  }

  get promotionGroupIndex(): number | undefined {
    return this.groupBy === 'promotion' ? 0 : undefined;
  }

  readonly currentDiscountAmount = (row: CustomerNegotiationDto): number | null =>
    this.discountAmount(row.current_discount_percent, row);

  readonly previousDiscountAmount = (row: CustomerNegotiationDto): number | null =>
    this.discountAmount(row.previous_discount_percent, row);

  readonly requestedDiscountAmount = (row: CustomerNegotiationDto): number | null =>
    this.discountAmount(row.requested_discount_percent, row);

  private discountAmount(percent: number | null | undefined, row: CustomerNegotiationDto): number | null {
    const cost = row.conversion_cost;
    if (row.price_data_missing || percent == null || cost == null ||
        !Number.isFinite(percent) || !Number.isFinite(cost) || cost <= 0) {
      return null;
    }
    return percent * cost / 100;
  }

  /** Show the Group-By control only when the current tab has rows to group (keeps it off empty/error/loading states). */
  get showGroupByBar(): boolean {
    const s = this.currentState;
    return s.loaded && !s.error && s.rows.length > 0;
  }

  /** True when `mode` is the active group-by option (drives the segmented control selection + a11y). */
  isGroupBySelected(mode: GroupByMode): boolean {
    return this.groupBy === mode;
  }

  /** dx-button-group item click → switch the group-by mode (Story 4.3, AC1). */
  onGroupByChange(e: { itemData?: { key?: GroupByMode } }): void {
    const mode = e?.itemData?.key;
    if (mode) {
      this.setGroupBy(mode);
    }
  }

  /**
   * Switch the group-by mode and re-group in place (no data reload — the [groupIndex] bindings on the
   * catalogue/supplier columns re-point as `groupBy` changes; AC1). Clearing the per-tab collapse sets
   * makes the freshly re-grouped view start fully expanded (autoExpandAll) across all three tabs. A
   * no-op when the mode is unchanged.
   */
  setGroupBy(mode: GroupByMode): void {
    if (mode === this.groupBy) {
      return;
    }
    this.groupBy = mode;
    for (const tab of this.tabs) {
      this.collapsed[tab].clear();
    }
  }

  /** The rows of a group (DevExtreme group cell), whether the group is expanded (`items`) or collapsed (`collapsedItems`). */
  private groupRows(group: any): CustomerNegotiationDto[] {
    return (group?.data?.items ?? group?.data?.collapsedItems ?? []) as CustomerNegotiationDto[];
  }

  /**
   * The §4.9 group-header label (Story 4.3, AC2). Catalogue mode → `CATALOGUE · N PRODUCTS`
   * (uppercase); Supplier mode → `Supplier name · N products`. Used by the shared `groupHeader`
   * template on whichever column is grouped.
   */
  groupHeaderLabel(group: any): string {
    const rows = this.groupRows(group);
    const n = rows.length;
    const noun = n === 1 ? 'product' : 'products';
    const name = group?.value || '—'; // `||` (not `??`) so an empty-string catalogue/supplier also falls back
    if (this.groupBy === 'supplier' || this.groupBy === 'promotion') {
      return `${name} · ${n} ${noun}`;
    }
    return `${name} · ${n} ${noun}`.toUpperCase();
  }

  /**
   * The collapsed-state aggregate pp-delta summary (Story 4.3, AC3): the mean of the group's Tier-1
   * `delta_pp`, formatted sign-aware to 1 dp as "+2.4 pp avg". Returns "—" when no row has a finite
   * delta. NEVER a `$` — `delta_pp` is a percentage-point figure (BR-07). Computed from the already-
   * loaded rows, so it is correct whether the group is expanded or collapsed.
   */
  groupAvgDeltaText(group: any): string {
    const r = this.groupAvgDeltaRounded(group);
    if (r === null) {
      return '—';
    }
    const sign = r > 0 ? '+' : '';
    return `${sign}${r.toFixed(1)} pp avg`;
  }

  /** Direction class for the group summary — green up / red down / muted flat (reuses the shared delta vocabulary). */
  groupAvgDeltaClass(group: any): string {
    const r = this.groupAvgDeltaRounded(group);
    if (r === null || r === 0) {
      return 'pf-delta--flat';
    }
    return r > 0 ? 'pf-delta--up' : 'pf-delta--down';
  }

  /**
   * The group mean delta rounded to the displayed 1 dp, or null when there is none. Rounding once here
   * (and normalising `-0` → `0` via `|| 0`) keeps the formatted text and the direction colour in
   * agreement at the rounding boundary — e.g. an avg of -0.04 is `0.0` (flat), not "-0.0 pp avg" shown red.
   */
  private groupAvgDeltaRounded(group: any): number | null {
    const avg = this.groupAvgDelta(group);
    return avg === null ? null : Math.round(avg * 10) / 10 || 0;
  }

  /**
   * Mean of the group's finite Tier-1 delta values, or null when none are finite. Uses `requested_delta_pp`
   * on the Update Requests tab (so the header matches that tab's "Change" body column = requested − current)
   * and `delta_pp` (current − previous baseline) on Pending / Accepted. Only the active tab's grid is in the
   * DOM, so `currentTab` reliably identifies which grid is rendering this group cell.
   */
  private groupAvgDelta(group: any): number | null {
    const field: 'requested_delta_pp' | 'delta_pp' =
      this.currentTab === 'update-requests' ? 'requested_delta_pp' : 'delta_pp';
    const deltas = this.groupRows(group)
      .map((r) => r[field])
      .filter((d): d is number => d !== null && d !== undefined && Number.isFinite(d));
    if (deltas.length === 0) {
      return null;
    }
    return deltas.reduce((a, b) => a + b, 0) / deltas.length;
  }

  /**
   * Accessible label for a group header (AC6) — name, product count, tier-count (catalogue mode) AND the
   * expanded/collapsed state. Built in natural case (not the visible label's ALL-CAPS, which some screen
   * readers spell out letter-by-letter) and with plain words rather than the "·" separator. Expanded state
   * is derivable from the group cell: DevExtreme populates `data.items` when expanded and `data.collapsedItems`
   * when collapsed.
   */
  groupAriaLabel(group: any): string {
    const rows = this.groupRows(group);
    const n = rows.length;
    const noun = n === 1 ? 'product' : 'products';
    const name = group?.value || '—';
    const state = group?.data?.items != null ? 'expanded' : 'collapsed';
    if (this.groupBy === 'supplier') {
      return `Supplier ${name}, ${n} ${noun}, ${state}`;
    }
    if (this.groupBy === 'promotion') {
      return `Promotion ${name}, ${n} ${noun}, ${state}`;
    }
    return `Catalogue ${name}, ${n} ${noun}, ${state}`;
  }

  /**
   * Track a group row's expand/collapse for the current tab (Story 4.3, AC4). DevExtreme fires
   * onRowExpanded/onRowCollapsed for BOTH group rows and master-detail data rows (the inline Update /
   * Counter forms). Group keys arrive as an array (e.g. `["WINE ESSENTIALS"]`); data-row keys are the
   * scalar `id`. We only track the array (group) keys so the inline forms never pollute the set.
   */
  private trackGroupCollapse(tab: CustomerTab, key: unknown, collapsedNow: boolean): void {
    if (!Array.isArray(key)) {
      return; // master-detail (data-row) expand/collapse — not a group
    }
    const k = JSON.stringify(key);
    if (collapsedNow) {
      this.collapsed[tab].add(k);
    } else {
      this.collapsed[tab].delete(k);
    }
  }

  onRowCollapsed(tab: CustomerTab, e: { key?: unknown }): void {
    this.trackGroupCollapse(tab, e?.key, true);
  }

  onRowExpanded(tab: CustomerTab, e: { key?: unknown }): void {
    this.trackGroupCollapse(tab, e?.key, false);
  }

  /**
   * On the first content-ready after a tab is (re-)entered, re-apply that tab's remembered collapsed
   * group keys (Story 4.3, AC4). Gated by `collapseReapply[tab]` so it runs once per entry and does not
   * fight a user's later expand/collapse or ordinary repaints. Stale keys from a previous group-by mode
   * simply match nothing (collapseRow is a no-op for an unknown key).
   */
  onGridContentReady(tab: CustomerTab, e: { component?: any }): void {
    if (!this.collapseReapply[tab]) {
      return;
    }
    const inst = e?.component;
    if (!inst) {
      return;
    }
    this.collapseReapply[tab] = false;
    for (const k of this.collapsed[tab]) {
      try {
        inst.collapseRow(JSON.parse(k));
      } catch {
        /* stale key under a different mode — ignore */
      }
    }
  }

  /**
   * dx-tabs items with count badges. The Pending Review / Update Requests badges always show the
   * unfiltered dashboard totals — the Status + Discount Delta filters are now native grid header filters
   * applied inside the grid, so they intentionally do NOT drive the badge. The Accepted badge reflects the
   * FILTERED row count only when the TAB-LOCAL date filter (Story 4.6) is active, else the dashboard total.
   */
  get tabItems(): Array<{ text: string; badge?: number }> {
    const next = [
      { text: 'Pending Review', badge: this.counts?.pending_review },
      { text: 'Update Requests', badge: this.counts?.update_requests },
      { text: 'Accepted', badge: this.dateFilterActive ? this.displayRows.accepted.length : this.counts?.accepted_total },
    ];
    // Return a STABLE array reference until the text/badges actually change. A getter that handed back a
    // fresh array every change-detection pass made dx-tabs re-create its tab elements on each CD, which
    // detached them mid-click — so clicking Update Requests / Accepted never registered (mirrors the
    // supplierOptions fix). Re-create the array only when its content changes so dx-tabs re-renders then.
    const key = next.map((i) => `${i.text}:${i.badge ?? ''}`).join('|');
    if (key !== this._tabItemsKey) {
      this._tabItemsKey = key;
      this._tabItems = next;
    }
    return this._tabItems;
  }
  private _tabItems: Array<{ text: string; badge?: number }> = [];
  private _tabItemsKey = '';

  /** The §3b "[N] flagged as outliers" sub-line shows only once a threshold flags ≥ 1 row. */
  get outlierSubCount(): number {
    return this.counts?.update_requests_outliers ?? 0;
  }

  /** Empty-state copy per tab (AC6). */
  emptyTitle(tab: CustomerTab): string {
    switch (tab) {
      case 'pending':
        return 'No products pending supplier review';
      case 'update-requests':
        return 'No update requests';
      default:
        return 'No accepted discount changes';
    }
  }

  /**
   * Text shown inside a grid that has no rows (DevExtreme `noDataText`). The grids stay rendered on
   * the loaded-empty state — instead of a separate full-width empty panel — so the grid toolbar
   * (supplier / group-by / delta / search) remains reachable when a filter narrows the list to zero
   * (e.g. picking a supplier with no rows on the current tab). Keeps the per-tab empty copy, plus the
   * Accepted date-range message (mirrors the former pf-empty-state copy).
   */
  gridNoDataText(tab: CustomerTab): string {
    if (tab === 'accepted' && this.acceptedDateEmpty) {
      return 'No accepted discounts in the selected date range.';
    }
    return this.emptyTitle(tab);
  }

  /**
   * dx-tabs item clicked — select the tab and lazy-load its data the first time it is shown.
   * One-way `[selectedIndex]` binding + this click handler (rather than two-way `[(selectedIndex)]`)
   * avoids dx-tabs writing a transient `-1` back into selectedIndex mid-render (NG0100).
   */
  onTabClick(e: { itemIndex?: number }): void {
    if (typeof e.itemIndex === 'number' && e.itemIndex >= 0) {
      this.selectTab(e.itemIndex);
    }
  }

  /** Select a tab by index and lazy-load it on first view. */
  selectTab(index: number): void {
    this.selectedIndex = index;
    const tab = this.currentTab;
    // The incoming tab's grid is (re-)created on entry (*ngSwitchCase) — re-apply its remembered
    // group-collapse state on its next content-ready (Story 4.3, AC4).
    this.collapseReapply[tab] = true;
    if (!this.state[tab].loaded && !this.state[tab].loading) {
      void this.loadTab(tab);
    }
  }

  /** Fetch the four summary-card counts. */
  private async loadCounts(): Promise<void> {
    this.countsLoading = true;
    this.countsError = false;
    try {
      const response: any = await firstValueFrom(
        this.crudService.newGetData(`${this.apiUrl}customer/dashboard`),
      );
      this.counts = (response?.data ?? null) as CustomerDashboardCounts | null;
    } catch (error) {
      console.error('Failed to load customer dashboard counts', error);
      this.counts = null;
      this.countsError = true;
    } finally {
      this.countsLoading = false;
    }
  }

  /**
   * Fetch the connected-supplier directory (Story 3.7 endpoint) for the filter pill bar (Story 4.2,
   * AC1). Mirrors loadCounts(): a plain GET via newGetData, tolerant of failure. A failed fetch leaves
   * the pill bar hidden ("All"-only behaviour) and never blocks the tabs.
   */
  private async loadSuppliers(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.crudService.newGetData(`${this.apiUrl}customer/suppliers`),
      );
      this.suppliers = (response?.data ?? []) as CustomerSupplierDto[];
    } catch (error) {
      console.error('Failed to load connected suppliers for the filter bar', error);
      this.suppliers = [];
    } finally {
      // Compute the dropdown's options ONCE here so its [dataSource] reference stays stable across
      // change detection (see `supplierOptions`). Active-review suppliers only, in the endpoint's
      // alpha order.
      this.supplierOptions = this.suppliers.filter((s) => s.active_reviews_count > 0);
      this.suppliersLoaded = true;
    }
  }

  /**
   * Select a supplier pill (or "All", code = null) (Story 4.2, AC2/AC3/AC4). Stores the selection in
   * the root-scoped filter service (BR-40 — survives tab switches + route re-entry, resets on reload),
   * busts every tab's cache so each re-fetches with the new filter on next view, and reloads the
   * current tab immediately. A no-op when the selection is unchanged.
   */
  selectSupplier(code: string | null): void {
    if (this.filterState.supplierCode === code) {
      return;
    }
    this.filterState.supplierCode = code;
    // Bump the generation so any in-flight load from a prior selection is discarded when it resolves.
    this.filterGeneration++;
    // A filter change invalidates every cached tab — otherwise switching tabs would show stale,
    // unfiltered rows (the tabs cache state[tab].rows + loaded). Reset them, then reload the current
    // tab now; the others reload lazily on next view via selectTab().
    for (const tab of this.tabs) {
      this.state[tab].loaded = false;
      this.state[tab].rows = [];
    }
    void this.loadTab(this.currentTab);
  }

  /** Fetch one tab's rows through the customer DTO list endpoint. */
  private async loadTab(tab: CustomerTab): Promise<void> {
    const s = this.state[tab];
    s.loading = true;
    s.error = false;
    // Capture the filter generation at the start of the load; if a newer supplier selection supersedes
    // it before the response arrives, this load's result is stale and must be discarded (the newer load
    // owns the state). See `filterGeneration`.
    const generation = this.filterGeneration;
    try {
      // Append the active supplier filter (Story 4.2) as a discrete query param, composing by AND with
      // ?tab= and the server-side default sort. Kept a plain param (not a DX filter blob) so it survives
      // Story 4.1's later remote-ops conversion. Encoded — supplier_code is a free-form varchar.
      const code = this.filterState.supplierCode;
      const supplierParam = code ? `&supplier_code=${encodeURIComponent(code)}` : '';
      const response: any = await firstValueFrom(
        this.crudService.newGetData(`${this.apiUrl}customer/negotiations?tab=${tab}${supplierParam}`),
      );
      if (generation !== this.filterGeneration) {
        return; // a newer supplier selection superseded this load — drop the stale rows
      }
      // The list endpoint returns the standard {success,data,message} envelope for <=500 rows
      // but a bare (gzip-compressed) array via sendResponseCompressed once a tab exceeds 500 rows.
      // Tolerate both shapes so large tabs don't silently render empty.
      s.rows = (Array.isArray(response) ? response : response?.data ?? []) as CustomerNegotiationDto[];
      s.loaded = true;
      // Recompute the chip-filtered view for this tab now its rows are in (Story 4.4). Composes with the
      // Story 4.2 supplier filter automatically — the rows here are already supplier-narrowed server-side.
      this.recomputeDisplayRows(tab);
    } catch (error) {
      if (generation !== this.filterGeneration) {
        return; // superseded — let the newer load manage state/error
      }
      console.error(`Failed to load customer ${tab} rows`, error);
      s.error = true;
      s.rows = [];
      this.recomputeDisplayRows(tab);
    } finally {
      // Only clear the in-flight flag if we still own this load — a superseding load manages its own.
      if (generation === this.filterGeneration) {
        s.loading = false;
      }
    }
  }

  /**
   * Rebuild one tab's `displayRows` from its loaded rows. The Status (Changed/Unchanged) and Discount
   * Delta filters are now native DevExtreme column header filters (applied inside the grid), so the only
   * client-side predicate left here is the tab-local Accepted date filter (Story 4.6); every other tab's
   * `displayRows` is just its loaded rows verbatim.
   */
  private recomputeDisplayRows(tab: CustomerTab): void {
    this.displayRows[tab] = this.state[tab].rows.filter((row) => this.rowPassesFilters(tab, row));
  }

  /**
   * The composed client-side filter predicate. Only the Story 4.6 Accepted date filter remains here (the
   * Status + Discount Delta filters moved to the grid header filters); it is tab-local (BR-40) — applied
   * ONLY on the Accepted tab — so every other tab passes every row through.
   */
  rowPassesFilters(tab: CustomerTab, row: CustomerNegotiationDto): boolean {
    return tab === 'accepted' ? this.rowAcceptedDateInRange(row) : true;
  }

  /**
   * The row's discount delta (current − previous baseline) as a finite number, or empty when null.
   * NOT `requested_delta_pp` (that is requested − current, the wrong metric for these bands). A null
   * delta (e.g. a price_data_missing row) yields no finite delta.
   */
  private tierDeltas(row: CustomerNegotiationDto): number[] {
    const d = row.delta_pp;
    return d !== null && d !== undefined && Number.isFinite(d) ? [d] : [];
  }

  // ===================================================================== Native header filters (Status + Change)
  // The Story 4.4 status chips (Changed/Unchanged) and the Story 4.5 Discount Delta range were retired from
  // the grid toolbar in favour of DevExtreme column header filters (funnel icons) on the Status and Change
  // columns. The grid filters client-side over the loaded (supplier-narrowed) rows; the helpers below supply
  // each column's calculated value, filter expression, and the funnel's preset item list. The Change-column
  // band is taken from the largest absolute tier delta. pp only, never a `$` (BR-07).

  /** Status header-filter items — the two buckets in the Status column funnel (Pending + Update Requests). */
  readonly statusHeaderItems: ReadonlyArray<{ text: string; value: 'changed' | 'unchanged' }> = [
    { text: 'Changed', value: 'changed' },
    { text: 'Unchanged', value: 'unchanged' },
  ];

  /** Discount-delta header-filter items — the fixed pp bands in the Change column funnel (all three tabs). */
  readonly deltaHeaderItems: ReadonlyArray<{ text: string; value: DeltaBand }> = [
    { text: '0–5 pp', value: '0-5' },
    { text: '5–10 pp', value: '5-10' },
    { text: '10–20 pp', value: '10-20' },
    { text: '>20 pp', value: '>20' },
  ];

  /**
   * Stable `[headerFilter]` config objects bound on the columns. Pre-built once (NOT an inline object
   * literal in the template) so DevExtreme gets a constant reference each change-detection pass and does
   * not re-initialise the funnel popup. `allowSearch:false` keeps the small fixed list free of a search box.
   */
  readonly statusHeaderFilter = { dataSource: this.statusHeaderItems, allowSearch: false };
  readonly deltaHeaderFilter = { dataSource: this.deltaHeaderItems, allowSearch: false };

  /** The requested delta (Update Requests "Change" column = requested − current). Finite only. */
  private requestedTierDeltas(row: CustomerNegotiationDto): number[] {
    const d = row.requested_delta_pp;
    return d !== null && d !== undefined && Number.isFinite(d) ? [d] : [];
  }

  /**
   * The pp band a set of tier deltas falls in, by the LARGEST absolute tier delta: 0–5 = (0,5], 5–10 =
   * (5,10], 10–20 = (10,20], >20 = (20,∞). Returns null for a flat (0 pp) row or one with no finite tier
   * delta (e.g. price_data_missing) — such a row matches no band, mirroring the retired §4.8 range.
   */
  private bandOf(deltas: number[]): DeltaBand | null {
    if (deltas.length === 0) {
      return null;
    }
    const abs = Math.max(...deltas.map((d) => Math.abs(d)));
    if (abs <= 0) return null;
    if (abs <= 5) return '0-5';
    if (abs <= 10) return '5-10';
    if (abs <= 20) return '10-20';
    return '>20';
  }

  /**
   * Status header-filter value (Status column). 'changed' when any configured tier moved off its baseline
   * (any |delta_pp| ≠ 0), else 'unchanged' — preserving the retired Story 4.4 chip semantics (a
   * price_data_missing / null-only row counts as unchanged). Arrow property so DevExtreme can invoke it
   * free of `this`.
   */
  statusFilterValue = (row: CustomerNegotiationDto): 'changed' | 'unchanged' =>
    this.tierDeltas(row).some((d) => d !== 0) ? 'changed' : 'unchanged';

  /** Change-column band value for the baseline delta (Pending + Accepted), or null when flat. */
  deltaBandValue = (row: CustomerNegotiationDto): DeltaBand | null => this.bandOf(this.tierDeltas(row));

  /** Change-column band value for the requested delta (Update Requests), or null when flat. */
  requestedDeltaBandValue = (row: CustomerNegotiationDto): DeltaBand | null =>
    this.bandOf(this.requestedTierDeltas(row));

  /**
   * Header-filter expressions (Story: native filter swap). Each maps a selected funnel value to a DevExtreme
   * filter using the column's calculated selector function — DevExtreme OR-combines multiple selected values
   * itself. Bound on the relevant column as `[calculateFilterExpression]` so filtering fires even though the
   * columns carry no plain `dataField`. Arrow properties so DevExtreme can invoke them free of `this`.
   */
  statusFilterExpression = (value: unknown, op: unknown): unknown => [this.statusFilterValue, op ?? '=', value];
  deltaFilterExpression = (value: unknown, op: unknown): unknown => [this.deltaBandValue, op ?? '=', value];
  requestedDeltaFilterExpression = (value: unknown, op: unknown): unknown => [
    this.requestedDeltaBandValue,
    op ?? '=',
    value,
  ];

  // ===================================================================== Accepted date filter (Story 4.6)

  /**
   * The five preset pills in display order (Story 4.6, AC1). `custom` reveals the From/To pickers.
   * The clear/"All" affordance is rendered separately (it maps to `clearDateFilter()` / `datePreset = null`).
   */
  readonly datePresetItems: ReadonlyArray<{ key: Exclude<DatePreset, null>; label: string }> = [
    { key: 'last7', label: 'Last 7 days' },
    { key: 'last30', label: 'Last 30 days' },
    { key: 'thisMonth', label: 'This month' },
    { key: 'lastMonth', label: 'Last month' },
    { key: 'custom', label: 'Custom range' },
  ];

  /** Working values for the custom From/To dx-date-box pickers (Date | null); applied on Apply. */
  customFrom: Date | null = null;
  customTo: Date | null = null;
  /** Quiet inline validation hint (e.g. From > To) — shown under the custom pickers (AC3). */
  dateRangeHint: string | null = null;

  /**
   * Test seam for "now" so the rolling/calendar presets are deterministic in specs (the spec overrides
   * this with a fixed clock). In production it is just `new Date()`.
   */
  private now: () => Date = () => new Date();

  /** True while any Accepted date filter is applied (a preset selected, incl. 'custom'). Drives the badge/UI. */
  get dateFilterActive(): boolean {
    return this.filterState.datePreset !== null;
  }

  /**
   * Whether the date filter bar should render (AC1) — ONLY on the Accepted tab (tab-local, BR-40), once
   * the tab has loaded rows (off empty/error/loading), and kept visible while a filter is active so the
   * clear control stays reachable (gate on the unfiltered `state.accepted.rows`, like the delta bar).
   */
  get showDateBar(): boolean {
    if (this.currentTab !== 'accepted') {
      return false;
    }
    const s = this.currentState;
    return (s.loaded && !s.error && s.rows.length > 0) || this.dateFilterActive;
  }

  /** True when `preset` is the active selection (drives `--active` + `aria-pressed`). */
  isDatePresetSelected(preset: Exclude<DatePreset, null>): boolean {
    return this.filterState.datePreset === preset;
  }

  /** True when no date filter is applied — drives the "All" pill's selected state. */
  get isDateAllSelected(): boolean {
    return this.filterState.datePreset === null;
  }

  /**
   * The active-summary text for the custom range (AC3a) — a compact "Accepted: 1 Jun – 9 Jun"
   * (open-ended bounds read "from …" / "until …"). Only ever rendered while the 'custom' preset is
   * active (the four rolling/calendar presets are conveyed by their selected pill), so a non-custom
   * preset returns '' here.
   */
  get dateFilterSummary(): string {
    const p = this.filterState.datePreset;
    if (p !== 'custom') {
      return '';
    }
    const f = this.filterState.dateFrom;
    const t = this.filterState.dateTo;
    if (f && t) {
      return `Accepted: ${this.fmtDate(f)} – ${this.fmtDate(t)}`;
    }
    if (f) {
      return `Accepted: from ${this.fmtDate(f)}`;
    }
    if (t) {
      return `Accepted: until ${this.fmtDate(t)}`;
    }
    return 'Custom range';
  }

  /** Compact day-month label for the summary pill, e.g. "1 Jun". Local-parses the `yyyy-MM-dd` bound (see parseIsoDate). */
  private fmtDate(iso: string): string {
    return this.parseIsoDate(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  }

  /**
   * Select a preset pill (AC1/AC2). Writing 'custom' reveals the pickers (keeping any entered range);
   * any other preset clears the custom range. Recomputes only the Accepted tab (tab-local, BR-40).
   */
  selectDatePreset(preset: Exclude<DatePreset, null>): void {
    this.filterState.datePreset = preset;
    if (preset !== 'custom') {
      this.filterState.dateFrom = null;
      this.filterState.dateTo = null;
      this.dateRangeHint = null;
    }
    this.recomputeDisplayRows('accepted');
  }

  /**
   * Apply the custom From/To range (AC3). Enforces From ≤ To (date-only) — an invalid range is NOT
   * applied and surfaces a quiet inline hint. A blank From or blank To leaves that bound open. Writes
   * the range (as `yyyy-MM-dd` strings) into the root service then recomputes the Accepted tab.
   */
  applyCustomRange(): void {
    const from = this.customFrom;
    const to = this.customTo;
    if (from && to && this.startOfDay(from).getTime() > this.startOfDay(to).getTime()) {
      this.dateRangeHint = 'From date must be on or before To date.';
      return;
    }
    this.dateRangeHint = null;
    this.filterState.datePreset = 'custom';
    this.filterState.dateFrom = from ? this.toIsoDate(from) : null;
    this.filterState.dateTo = to ? this.toIsoDate(to) : null;
    this.recomputeDisplayRows('accepted');
  }

  /** Clear the date filter (AC3a) — reset the service state + the pickers, then recompute the Accepted tab. */
  clearDateFilter(): void {
    this.filterState.clearDateFilter();
    this.customFrom = null;
    this.customTo = null;
    this.dateRangeHint = null;
    this.recomputeDisplayRows('accepted');
  }

  /**
   * Rehydrate the custom pickers from the persisted root-service state (Story 4.6, AC7). Called on init so
   * a route re-entry that restored a custom range shows the picked dates in the From/To boxes.
   */
  private syncDateInputsFromState(): void {
    this.customFrom = this.filterState.dateFrom ? new Date(this.filterState.dateFrom) : null;
    this.customTo = this.filterState.dateTo ? new Date(this.filterState.dateTo) : null;
  }

  /**
   * Whether a row's accepted date (`effective_date`) falls in the active window (Story 4.6, AC2/AC3).
   * Date-only inclusive comparison: the row date and both bounds are normalised to start-of-day so a row
   * accepted at any time on a boundary day matches. No active filter ⇒ pass-through. A null `effective_date`
   * never matches (defensive — Accepted rows always have one).
   */
  rowAcceptedDateInRange(row: CustomerNegotiationDto): boolean {
    const preset = this.filterState.datePreset;
    if (preset === null) {
      return true;
    }
    const range = preset === 'custom' ? this.resolveCustomRange() : this.presetRange(preset, this.now());
    if (!range || (range.from === null && range.to === null)) {
      return true; // invalid custom range, or a custom range with both bounds open ⇒ no filter
    }
    if (!row.effective_date) {
      return false;
    }
    const d = this.startOfDay(new Date(row.effective_date)).getTime();
    if (Number.isNaN(d)) {
      return false;
    }
    if (range.from !== null && d < this.startOfDay(range.from).getTime()) {
      return false;
    }
    if (range.to !== null && d > this.startOfDay(range.to).getTime()) {
      return false;
    }
    return true;
  }

  /**
   * The inclusive `[from, to]` window for a non-custom preset (Story 4.6, BR-13), computed from a single
   * "now" so specs are deterministic. Both bounds are concrete days (date-only comparison strips the time):
   *  - Last 7 days  → now − 7 days … now (rolling);
   *  - Last 30 days → now − 30 days … now (rolling);
   *  - This month   → 1st of now's month … now;
   *  - Last month   → 1st of the previous month … last day of the previous month.
   */
  presetRange(preset: Exclude<DatePreset, null | 'custom'>, now: Date): { from: Date | null; to: Date | null } {
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (preset) {
      case 'last7': {
        const from = this.startOfDay(now);
        from.setDate(from.getDate() - 7);
        return { from, to: this.startOfDay(now) };
      }
      case 'last30': {
        const from = this.startOfDay(now);
        from.setDate(from.getDate() - 30);
        return { from, to: this.startOfDay(now) };
      }
      case 'thisMonth':
        return { from: new Date(y, m, 1), to: this.startOfDay(now) };
      case 'lastMonth':
        // Day 0 of the current month = the last day of the previous month.
        return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) };
      default:
        // Unreachable for the four narrowed presets; a defensive open range (no filter) keeps the
        // non-nullable return honest if a new preset is ever added without a case here.
        return { from: null, to: null };
    }
  }

  /** Resolve the persisted custom range to Dates; returns null when From > To (defensive — apply rejects it). */
  private resolveCustomRange(): { from: Date | null; to: Date | null } | null {
    const from = this.filterState.dateFrom ? this.parseIsoDate(this.filterState.dateFrom) : null;
    const to = this.filterState.dateTo ? this.parseIsoDate(this.filterState.dateTo) : null;
    if (from && to && from.getTime() > to.getTime()) {
      return null;
    }
    return { from, to };
  }

  /**
   * Parse a `yyyy-MM-dd` date-only string to a LOCAL start-of-day Date. Deliberately NOT `new Date(iso)`:
   * the JS spec parses a bare `yyyy-MM-dd` as UTC midnight, which `startOfDay` would then read back in local
   * time and shift a calendar day in any negative-UTC-offset zone — misfiltering boundary rows by a day.
   * Building `new Date(y, m-1, d)` keeps the stored bounds on the same local calendar day as the row dates
   * (which arrive as local-parsed datetimes), so the inclusive date-only comparison stays correct everywhere.
   */
  private parseIsoDate(iso: string): Date {
    const [y, mo, d] = iso.split('-').map(Number);
    return new Date(y, mo - 1, d);
  }

  /** Midnight (local) of the given date — the date-only normaliser used for all window comparisons. */
  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Format a Date to a `yyyy-MM-dd` date-only string for the root service (locale-stable). */
  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const mo = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${mo}-${day}`;
  }

  /**
   * True when the Accepted tab is non-empty but a date filter has narrowed it to zero rows (AC4) — drives
   * the date-specific empty copy "No accepted discounts in the selected date range." (vs. the generic
   * "No accepted discount changes" used when there are genuinely no accepted rows).
   */
  get acceptedDateEmpty(): boolean {
    return (
      this.currentTab === 'accepted' &&
      this.dateFilterActive &&
      this.state.accepted.loaded &&
      !this.state.accepted.error &&
      this.state.accepted.rows.length > 0 &&
      this.displayRows.accepted.length === 0
    );
  }

  // ===================================================================== shared helpers

  /**
   * Validate a discount value: numeric, 0 ≤ x < 100, ≤ 2 decimal places (AC4/AC5; mirrors the
   * server % rules + the promotion-discount-view validator). Returns an inline message, or null.
   */
  validatePercent(value: number | null): string | null {
    if (value === null || value === undefined) {
      return 'Enter a discount percentage';
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return 'Must be a number';
    }
    if (value < 0 || value >= 100) {
      return 'Must be 0 or more and less than 100';
    }
    if (Number(value.toFixed(2)) !== value) {
      return 'Maximum 2 decimal places';
    }
    return null;
  }

  /** Live pp helper text: "Current: 12.5% → Updated: 13.0% (Δ +0.5 pp)" (AC4). '' when no input yet. */
  deltaHelper(current: number | null, updated: number | null): string {
    if (updated === null || updated === undefined || !Number.isFinite(updated)) {
      return '';
    }
    // No baseline to compare against — show the updated value without a fabricated Δ.
    if (current === null || current === undefined || !Number.isFinite(current)) {
      return `Current: — → Updated: ${updated.toFixed(1)}%`;
    }
    const delta = updated - current;
    const sign = delta > 0 ? '+' : '';
    return `Current: ${this.fmt(current)} → Updated: ${updated.toFixed(1)}% (Δ ${sign}${delta.toFixed(1)} pp)`;
  }

  /** Direction class for the live helper — green larger / red smaller / muted unchanged (BR-07, % only). */
  deltaClass(current: number | null, updated: number | null): string {
    if (
      updated === null || updated === undefined || !Number.isFinite(updated) ||
      current === null || current === undefined || !Number.isFinite(current)
    ) {
      return 'pf-delta--flat';
    }
    const delta = updated - current;
    if (delta > 0) return 'pf-delta--up';
    if (delta < 0) return 'pf-delta--down';
    return 'pf-delta--flat';
  }

  private fmt(value: number | null): string {
    return value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}%`;
  }

  /** Build the per-row confirmed-discount toast text: "Discount 6.5%". */
  private discountSummary(dto: CustomerNegotiationDto): string {
    return `Discount ${this.fmt(dto.current_discount_percent)}`;
  }

  /** True when a row could not be acted on by the last bulk action — drives its inline error (AC9). */
  isFailed(id: number): boolean {
    return this.failedIds.has(id);
  }

  /**
   * Pull a human-readable message out of a failed HTTP response (the `{success,message,data}`
   * envelope from BaseController::sendError), falling back to a default. Prefers the friendly
   * detail in `data` (e.g. "The selection mixes single- and two-tier catalogues…") over the
   * generic top-level `message` ("Validation Error.").
   */
  private serverMessage(error: any, fallback: string): string {
    const body = error?.error;
    if (body && typeof body === 'object') {
      const data = body.data;
      if (Array.isArray(data) && typeof data[0] === 'string') {
        return data[0];
      }
      if (data && typeof data === 'object') {
        const first = Object.values(data)[0];
        if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
        if (typeof first === 'string') return first;
      }
      if (typeof body.message === 'string' && body.message && body.message !== 'Validation Error.') {
        return body.message;
      }
    }
    return fallback;
  }

  /** A green row-flash class is applied to a just-accepted row for ≈300ms before it leaves the tab. */
  onPendingRowPrepared(e: { rowType?: string; data?: CustomerNegotiationDto; rowElement?: HTMLElement }): void {
    if (e.rowType === 'data' && e.data && e.rowElement) {
      // --amber-dim outlier row background (Story 3.6, AC3) — driven by the server-computed is_outlier
      // (false until a threshold is set). The "Outlier" badge + breached-tier red delta carry the
      // meaning too, so colour is never the sole signal (NFR8).
      if (e.data.is_outlier) {
        e.rowElement.classList.add('pf-row-outlier');
      }
      if (this.flashingIds.has(e.data.id)) {
        e.rowElement.classList.add('pf-row-flash');
      }
      // Mark a row a bulk action could not act on (AC9 — failed rows stay in place with an inline error).
      if (this.failedIds.has(e.data.id)) {
        e.rowElement.classList.add('pf-row-error');
      }
    }
  }

  /** After an action, refresh the cards and invalidate the other tabs so they re-fetch on next view. */
  private async refreshAfterAction(): Promise<void> {
    this.state.accepted.loaded = false;
    this.state['update-requests'].loaded = false;
    await this.loadCounts();
  }

  // ===================================================================== single Accept

  /** Accept a single Pending Review row — confirm the current discount(s) (AC1). */
  async accept(row: CustomerNegotiationDto): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/${row.id}/accept`, {}),
      );
      const data: any = response?.data ?? {};
      if (data.already_accepted) {
        notify('This product has already been accepted.', 'warning', 2500);
        this.removePendingRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      const dto: CustomerNegotiationDto = data.negotiation ?? row;
      this.flashAndRemove(row.id);
      notify(`Discount accepted — ${this.discountSummary(dto)}`, 'success', 2500);
      await this.refreshAfterAction();
    } catch (error: any) {
      console.error('Failed to accept discount', error);
      // A 404 means the row is no longer pending / out of scope — refresh it out rather than erroring.
      if (error?.status === 404) {
        notify('This product is no longer pending review.', 'warning', 2500);
        this.removePendingRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      notify(this.serverMessage(error, 'Could not accept the discount — please try again'), 'error', 6000);
    }
  }

  /** Flash the row green (~300ms) then remove it from the Pending Review list (it moves to Accepted). */
  private flashAndRemove(id: number): void {
    this.flashingIds.add(id);
    const timer = setTimeout(() => {
      this.flashTimers.delete(timer);
      this.flashingIds.delete(id);
      this.removePendingRow(id);
    }, 320);
    this.flashTimers.add(timer);
  }

  private removePendingRow(id: number): void {
    this.state.pending.rows = this.state.pending.rows.filter((r) => r.id !== id);
    this.selectedPendingRows = this.selectedPendingRows.filter((r) => r.id !== id);
    // The grid binds to displayRows.pending, not state.pending.rows — recompute so the removal shows
    // (Story 4.4: the chip-filtered view must track every mutation of the loaded rows, not just chip toggles).
    this.recomputeDisplayRows('pending');
  }

  // ===================================================================== Send Reminder (Story 3.5)

  /**
   * Send (or resend) a manual reminder to a Pending Review row's supplier (AC1/AC5/AC6). SINGLE click
   * — there is NO confirmation dialog. On success the row's `reminder_sent_at` is set from the returned
   * DTO (the "Reminder Sent" sub-badge appears and the link relabels to "Resend Reminder"), and an
   * accent "Reminder sent to [Supplier Name]" toast shows. A failed send (no email / SES error) leaves
   * the row unchanged (no badge, link stays "Send Reminder") and surfaces a red toast — no auto-retry
   * (BR-NTF-03). An already-handled row (no longer pending) is refreshed out with an explanatory toast.
   */
  async remind(row: CustomerNegotiationDto): Promise<void> {
    if (this.remindingIds.has(row.id)) {
      return; // a reminder is already in flight for this row (the link is disabled, but guard anyway)
    }
    this.remindingIds.add(row.id);
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/${row.id}/remind`, {}),
      );
      const data: any = response?.data ?? {};

      // Already-handled (AC4) — the row is no longer pending; refresh it out and explain.
      if (data.already_handled) {
        const message = data.reason === 'already_accepted'
          ? 'This product has already been accepted.'
          : 'This product is no longer pending review.';
        notify(message, 'warning', 2500);
        this.removePendingRow(row.id);
        await this.refreshAfterAction();
        return;
      }

      // Send failed (no email on file / SES error, AC5) — surface inline, leave the row unchanged
      // (no badge, link stays "Send Reminder"), no auto-retry.
      if (!data.sent) {
        const message = data.reason === 'no_email'
          ? `Couldn't send the reminder — no email on file for ${row.supplier_name ?? 'this supplier'}.`
          : `Couldn't send the reminder. Please try again.`;
        notify(message, 'error', 6000);
        return;
      }

      // Success (AC6) — update the row from the returned DTO (sub-badge appears, link → "Resend
      // Reminder"); accent toast. Fall back to stamping the row locally if no DTO came back.
      const dto: CustomerNegotiationDto | null = data.negotiation ?? null;
      if (dto) {
        this.replacePendingRow(dto);
      } else {
        row.reminder_sent_at = new Date().toISOString();
      }
      notify(`Reminder sent to ${row.supplier_name ?? 'supplier'}`, 'info', 2500);
    } catch (error: any) {
      console.error('Failed to send reminder', error);
      // A 404 means the row is no longer pending / out of scope — refresh it out rather than erroring.
      if (error?.status === 404) {
        notify('This product is no longer pending review.', 'warning', 2500);
        this.removePendingRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      notify(this.serverMessage(error, `Couldn't send the reminder. Please try again.`), 'error', 6000);
    } finally {
      this.remindingIds.delete(row.id);
    }
  }

  // ===================================================================== Confirm an update request (Story 3.3)

  /**
   * Confirm a supplier's update request on the Update Requests tab (AC3/AC7) — accept the supplier's
   * requested discount(s) verbatim. On success the row flashes `--green-dim`, animates out, and the
   * product moves to the Accepted tab; a green "Update confirmed" toast lists each confirmed discount
   * and the cards re-fetch. The already-handled path (AC5) keys its toast off the `reason`
   * discriminator; an error leaves the row in place (no optimistic mutation, no auto-retry).
   */
  async confirm(row: CustomerNegotiationDto): Promise<void> {
    if (this.confirmingIds.has(row.id)) {
      return; // a Confirm is already in flight for this row (the button is disabled, but guard anyway)
    }
    this.confirmingIds.add(row.id);
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/${row.id}/confirm`, {}),
      );
      const data: any = response?.data ?? {};
      if (data.already_handled) {
        const message = data.reason === 'already_accepted'
          ? 'This product has already been accepted.'
          : 'This update request has been withdrawn by the supplier.';
        notify(message, 'warning', 2500);
        this.removeUpdateRequestRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      const dto: CustomerNegotiationDto | null = data.negotiation ?? null;
      this.flashAndRemoveUpdateRequest(row.id);
      notify(`Update confirmed — ${this.confirmedSummary(row, dto)}`, 'success', 2500);
      await this.refreshAfterAction();
    } catch (error: any) {
      console.error('Failed to confirm update request', error);
      // A 404 means the row is no longer an update request / out of scope — refresh it out.
      if (error?.status === 404) {
        notify('This update request is no longer available.', 'warning', 2500);
        this.removeUpdateRequestRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      // 422 tier-integrity / 5xx / network — leave the row at update_requested, no retry (AC7).
      notify(this.serverMessage(error, 'Could not confirm the update request — please try again'), 'error', 6000);
    } finally {
      this.confirmingIds.delete(row.id);
    }
  }

  /**
   * Confirmed-discount toast text. Prefers the accepted DTO (its `current_*` now equals the
   * confirmed = requested value); falls back to the row's `requested_*` if no DTO came back.
   */
  private confirmedSummary(row: CustomerNegotiationDto, dto: CustomerNegotiationDto | null): string {
    if (dto) {
      return this.discountSummary(dto);
    }
    return `Discount ${this.fmt(row.requested_discount_percent ?? null)}`;
  }

  /** Flash the row green (~300ms) then remove it from the Update Requests list (it moves to Accepted). */
  private flashAndRemoveUpdateRequest(id: number): void {
    this.confirmFlashingIds.add(id);
    // Repaint the just-confirmed row so onRowPrepared re-fires and applies the `pf-row-flash` class —
    // adding to confirmFlashingIds alone does not trigger a render, so without this the flash never
    // appears. Guarded: the grid instance is absent in unit tests (the ViewChild is detached there).
    const grid = this.updateRequestsGrid?.instance;
    if (grid) {
      const rowIndex = grid.getRowIndexByKey(id);
      if (rowIndex >= 0) {
        grid.repaintRows([rowIndex]);
      }
    }
    const timer = setTimeout(() => {
      this.flashTimers.delete(timer);
      this.confirmFlashingIds.delete(id);
      this.removeUpdateRequestRow(id);
    }, 320);
    this.flashTimers.add(timer);
  }

  private removeUpdateRequestRow(id: number): void {
    this.state['update-requests'].rows = this.state['update-requests'].rows.filter((r) => r.id !== id);
    // The grid binds to displayRows['update-requests'] — recompute so the removal shows (Story 4.4).
    this.recomputeDisplayRows('update-requests');
  }

  /** Green row-flash on a just-confirmed / just-countered Update Requests row for ≈300ms before it leaves the tab. */
  onUpdateRequestRowPrepared(e: { rowType?: string; data?: CustomerNegotiationDto; rowElement?: HTMLElement }): void {
    if (e.rowType === 'data' && e.data && e.rowElement) {
      // --amber-dim outlier row background (Story 3.6, AC3) — the Update Requests tab IS flagged
      // (along with Pending Review); only the Accepted tab is never flagged.
      if (e.data.is_outlier) {
        e.rowElement.classList.add('pf-row-outlier');
      }
      if (this.confirmFlashingIds.has(e.data.id)) {
        e.rowElement.classList.add('pf-row-flash');
      }
    }
  }

  // ===================================================================== Counter an update request (Story 3.4)

  /**
   * Open the inline counter form below an Update Requests row (collapsing any other open form first)
   * (AC1/AC2). The form is pre-filled with the supplier's REQUESTED discount(s) as the starting point
   * — the helper then reads "Supplier requested: X% → Counter: Y% (Δ pp)" as the customer revises.
   */
  openCounter(row: CustomerNegotiationDto): void {
    const grid = this.updateRequestsGrid?.instance;
    if (this.counterRow && this.counterRow.id !== row.id) {
      grid?.collapseRow(this.counterRow.id);
    }
    this.counterRow = row;
    this.counterForm = {
      value: row.requested_discount_percent ?? null,
      error: null,
      submitting: false,
    };
    grid?.expandRow(row.id);
  }

  /** Cancel the inline counter form. */
  cancelCounter(): void {
    if (this.counterRow) {
      this.updateRequestsGrid?.instance.collapseRow(this.counterRow.id);
    }
    this.counterRow = null;
    this.counterForm = this.emptyForm();
  }

  onCounterValueChanged(event: { value?: number | null }): void {
    const value = event?.value ?? null;
    this.counterForm.value = value;
    this.counterForm.error = this.validatePercent(value);
  }

  /**
   * Live counter helper: "Supplier requested: 15.0% → Counter: 13.0% (Δ -2.0 pp)" (AC2). The baseline
   * is the supplier's REQUESTED value (Story 3.3's enriched requested_*), NOT the current — this is
   * the key difference from the Pending Review update helper. '' when no input yet.
   */
  counterHelper(requested: number | null, counter: number | null): string {
    if (counter === null || counter === undefined || !Number.isFinite(counter)) {
      return '';
    }
    if (requested === null || requested === undefined || !Number.isFinite(requested)) {
      return `Supplier requested: — → Counter: ${counter.toFixed(1)}%`;
    }
    const delta = counter - requested;
    const sign = delta > 0 ? '+' : '';
    return `Supplier requested: ${this.fmt(requested)} → Counter: ${counter.toFixed(1)}% (Δ ${sign}${delta.toFixed(1)} pp)`;
  }

  /**
   * Send the customer's revised discount(s) back to the supplier (AC3/AC7). On success the row
   * animates out of Update Requests and the product reappears on Pending Review (the cards re-fetch:
   * Update Requests −1, Products Pending Review +1). The already-handled path (AC5) keys its toast off
   * the `reason` discriminator; validation keeps the form open; an error leaves the row in place.
   */
  async submitCounter(): Promise<void> {
    const row = this.counterRow;
    if (!row) {
      return;
    }
    this.counterForm.error = this.validatePercent(this.counterForm.value);
    if (this.counterForm.error) {
      return; // keep the form open with inline messages (AC7)
    }

    const payload: { discount_percent: number } = {
      discount_percent: this.counterForm.value as number,
    };

    this.counterForm.submitting = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/${row.id}/counter`, payload),
      );
      const data: any = response?.data ?? {};
      if (data.already_handled) {
        const message = data.reason === 'already_accepted'
          ? 'This product has already been accepted.'
          : 'This update request has been withdrawn by the supplier.';
        notify(message, 'warning', 2500);
        this.cancelCounter();
        this.removeUpdateRequestRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      // Success — collapse the form, animate the row out (it reappears on Pending Review), toast + cards.
      this.cancelCounter();
      this.flashAndRemoveUpdateRequest(row.id);
      notify(`Update sent to ${row.supplier_name ?? 'supplier'}`, 'success', 2500);
      await this.refreshAfterAction();
    } catch (error: any) {
      console.error('Failed to send counter', error);
      // A 404 means the row is no longer an update request / out of scope — refresh it out.
      if (error?.status === 404) {
        notify('This update request is no longer available.', 'warning', 2500);
        this.cancelCounter();
        this.removeUpdateRequestRow(row.id);
        await this.refreshAfterAction();
        return;
      }
      // 422 tier mismatch / 5xx / network — keep the form open, leave the row at update_requested (AC7).
      notify(this.serverMessage(error, 'Could not send the update — please try again'), 'error', 6000);
    } finally {
      this.counterForm.submitting = false;
    }
  }

  // ===================================================================== single Update (inline)

  /** Open the inline Update form below a row (collapsing any other open form first) (AC4). */
  openUpdate(row: CustomerNegotiationDto): void {
    const grid = this.pendingGrid?.instance;
    if (this.updateRow && this.updateRow.id !== row.id) {
      grid?.collapseRow(this.updateRow.id);
    }
    this.updateRow = row;
    this.updateForm = {
      value: row.current_discount_percent,
      error: null,
      submitting: false,
    };
    grid?.expandRow(row.id);
  }

  /** Cancel the inline Update form. */
  cancelUpdate(): void {
    if (this.updateRow) {
      this.pendingGrid?.instance.collapseRow(this.updateRow.id);
    }
    this.updateRow = null;
    this.updateForm = this.emptyForm();
  }

  onUpdateValueChanged(event: { value?: number | null }): void {
    const value = event?.value ?? null;
    this.updateForm.value = value;
    this.updateForm.error = this.validatePercent(value);
  }

  /** Send the revised discount back to the supplier — the row stays Pending Review (AC4). */
  async submitUpdate(): Promise<void> {
    const row = this.updateRow;
    if (!row) {
      return;
    }
    this.updateForm.error = this.validatePercent(this.updateForm.value);
    if (this.updateForm.error) {
      return; // keep the form open with inline messages (AC9)
    }

    const payload: { discount_percent: number } = {
      discount_percent: this.updateForm.value as number,
    };

    this.updateForm.submitting = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/${row.id}/update`, payload),
      );
      const data: any = response?.data ?? {};
      if (data.already_handled) {
        notify('This product is no longer pending review.', 'warning', 2500);
        this.removePendingRow(row.id);
        this.cancelUpdate();
        await this.refreshAfterAction();
        return;
      }
      const dto: CustomerNegotiationDto = data.negotiation;
      if (dto) {
        this.replacePendingRow(dto); // revised current + recomputed delta, still pending
      }
      notify(`Update sent to ${row.supplier_name ?? 'supplier'}`, 'success', 2500);
      this.cancelUpdate();
    } catch (error: any) {
      console.error('Failed to send update', error);
      // Surface the server's reason (e.g. tier mismatch, price data missing); keep the form open.
      notify(this.serverMessage(error, 'Could not send the update — please try again'), 'error', 6000);
    } finally {
      this.updateForm.submitting = false;
    }
  }

  private replacePendingRow(dto: CustomerNegotiationDto): void {
    // Action responses carry updated percentages, but not the dashboard's pricing/
    // promotion metadata. Preserve those so dollar values recalculate immediately.
    this.state.pending.rows = this.state.pending.rows.map((r) => (r.id === dto.id ? { ...r, ...dto } : r));
    // The grid binds to displayRows.pending — recompute so the revised values/delta show (Story 4.4).
    this.recomputeDisplayRows('pending');
  }

  // ===================================================================== bulk selection

  onPendingSelectionChanged(e: { selectedRowsData?: CustomerNegotiationDto[] }): void {
    this.selectedPendingRows = e.selectedRowsData ?? [];
    // Once the user starts a fresh (non-empty) selection to retry, drop the stale inline error marks.
    // (The programmatic clearSelection() after a bulk action fires this with an empty selection, so
    // the just-set failedIds marks are preserved.)
    if (this.selectedPendingRows.length > 0 && this.failedIds.size > 0) {
      this.failedIds.clear();
      this.pendingGrid?.instance.repaint();
    }
  }

  get selectedCount(): number {
    return this.selectedPendingRows.length;
  }

  /** Bulk Update is enabled whenever at least one product is selected (mixed selections allowed). */
  get bulkUpdateEnabled(): boolean {
    return this.selectedCount > 0;
  }

  get bulkUpdateDisabledHint(): string {
    if (this.selectedCount === 0) {
      return 'Select one or more products to bulk update';
    }
    return 'Apply one revised discount to all selected products';
  }

  // ===================================================================== Bulk Accept modal

  openBulkAccept(): void {
    if (this.selectedCount === 0) {
      return;
    }
    this.bulkAcceptVisible = true;
  }

  /** Products-to-accept count. */
  get bulkProductsCount(): number {
    return this.selectedCount;
  }

  /** Distinct suppliers affected by the current selection. */
  get bulkSupplierCount(): number {
    return new Set(this.selectedPendingRows.map((r) => r.supplier_code)).size;
  }

  /** Per-catalogue breakdown, e.g. "12 All Products, 30 Specials". */
  get bulkCatalogueBreakdown(): string {
    const groups = new Map<string, number>();
    for (const r of this.selectedPendingRows) {
      const key = r.catalogue ?? 'Uncategorised';
      groups.set(key, (groups.get(key) ?? 0) + 1);
    }
    return Array.from(groups.entries())
      .map(([cat, count]) => `${count} ${cat}`)
      .join(', ');
  }

  /**
   * Mean pp delta across the selection — each product contributes its single `delta_pp`. Null when
   * no selected row has a finite delta.
   */
  get bulkAvgDelta(): number | null {
    const perProduct: number[] = [];
    for (const r of this.selectedPendingRows) {
      if (r.delta_pp !== null && Number.isFinite(r.delta_pp)) {
        perProduct.push(r.delta_pp);
      }
    }
    if (perProduct.length === 0) {
      return null;
    }
    return perProduct.reduce((a, b) => a + b, 0) / perProduct.length;
  }

  get bulkAvgDeltaText(): string {
    const avg = this.bulkAvgDelta;
    if (avg === null) {
      return '—';
    }
    const sign = avg > 0 ? '+' : '';
    return `${sign}${avg.toFixed(1)} pp`;
  }

  get bulkAvgDeltaClass(): string {
    const avg = this.bulkAvgDelta;
    if (avg === null || avg === 0) return 'pf-delta--flat';
    return avg > 0 ? 'pf-delta--up' : 'pf-delta--down';
  }

  /** Any selected row already flagged as an outlier (false until Story 3.6 sets a threshold). */
  get bulkHasOutlier(): boolean {
    return this.selectedPendingRows.some((r) => r.is_outlier === true);
  }

  /** Confirm the Bulk Accept — one batch accept, one Effective Date (AC7). */
  async confirmBulkAccept(): Promise<void> {
    const ids = this.selectedPendingRows.map((r) => r.id);
    if (ids.length === 0) {
      return;
    }
    this.failedIds.clear();
    this.bulkAcceptSubmitting = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/bulk-accept`, { ids }),
      );
      const data: any = response?.data ?? {};
      // Drive removal from the server's authoritative accepted list (NOT requested-minus-failed) so
      // a row the server didn't flip is never optimistically removed.
      const accepted: CustomerNegotiationDto[] = data.accepted ?? [];
      const acceptedIds = new Set<number>(accepted.map((d) => d.id));
      const acceptedCount: number = data.accepted_count ?? acceptedIds.size;
      const failedIds: number[] = data.failed_ids ?? ids.filter((id) => !acceptedIds.has(id));
      this.state.pending.rows = this.state.pending.rows.filter((r) => !acceptedIds.has(r.id));
      // The grid binds to displayRows.pending — recompute so the bulk-accepted rows leave the view (Story 4.4).
      this.recomputeDisplayRows('pending');
      // Mark the failed rows so they stay in place with an inline error (AC9).
      this.failedIds = new Set<number>(failedIds);
      this.clearSelection();
      this.bulkAcceptVisible = false;
      this.pendingGrid?.instance.repaint();

      if (failedIds.length === 0) {
        notify(`${acceptedCount} product(s) accepted`, 'success', 2500);
      } else {
        notify(`${acceptedCount} accepted, ${failedIds.length} failed`, 'warning', 4000);
      }
      await this.refreshAfterAction();
    } catch (error: any) {
      console.error('Failed to bulk accept', error);
      notify(this.serverMessage(error, 'Could not accept the selected products — please try again'), 'error', 6000);
    } finally {
      this.bulkAcceptSubmitting = false;
    }
  }

  // ===================================================================== Bulk Update modal

  openBulkUpdate(): void {
    if (!this.bulkUpdateEnabled) {
      return;
    }
    this.bulkUpdateForm = this.emptyForm();
    this.bulkUpdateVisible = true;
  }

  onBulkUpdateValueChanged(event: { value?: number | null }): void {
    const value = event?.value ?? null;
    this.bulkUpdateForm.value = value;
    this.bulkUpdateForm.error = this.validatePercent(value);
  }

  /** Apply the revised discount to every selected row — rows stay Pending Review (AC8). */
  async submitBulkUpdate(): Promise<void> {
    if (!this.bulkUpdateEnabled) {
      return;
    }
    this.bulkUpdateForm.error = this.validatePercent(this.bulkUpdateForm.value);
    if (this.bulkUpdateForm.error) {
      return;
    }

    const ids = this.selectedPendingRows.map((r) => r.id);
    const payload: { ids: number[]; discount_percent: number } = {
      ids,
      discount_percent: this.bulkUpdateForm.value as number,
    };

    this.failedIds.clear();
    this.bulkUpdateForm.submitting = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}customer/negotiations/bulk-update`, payload),
      );
      const data: any = response?.data ?? {};
      const updated: CustomerNegotiationDto[] = data.updated ?? [];
      const updatedCount: number = data.updated_count ?? updated.length;
      const failedIds: number[] = data.failed_ids ?? [];
      // Replace the revised rows in place (they stay Pending Review with the new values + delta).
      for (const dto of updated) {
        this.replacePendingRow(dto);
      }
      // Mark the failed rows so they stay in place with an inline error (AC9).
      this.failedIds = new Set<number>(failedIds);
      this.clearSelection();
      this.bulkUpdateVisible = false;
      this.pendingGrid?.instance.repaint();

      if (failedIds.length === 0) {
        notify(`${updatedCount} product(s) updated`, 'success', 2500);
      } else {
        notify(`${updatedCount} updated, ${failedIds.length} failed`, 'warning', 4000);
      }
      await this.refreshAfterAction();
    } catch (error: any) {
      console.error('Failed to bulk update', error);
      // Surface the server's reason (e.g. mixed catalogues / tier mismatch on a stale selection).
      notify(this.serverMessage(error, 'Could not update the selected products — please try again'), 'error', 6000);
    } finally {
      this.bulkUpdateForm.submitting = false;
    }
  }

  private clearSelection(): void {
    this.selectedPendingRows = [];
    this.pendingGrid?.instance.clearSelection();
  }
}
